/**
 * Pack Projects Section
 *
 * Lists projects linked to a feature pack with unlink action. The add controls
 * sit in a toolbar strip attached to the top of the table: a ProjectSelector
 * (same UX as project config and workspaces) that hides already-linked
 * projects, plus a collapsible lookup by external ref for projects the search
 * can't reach.
 */

import { useState, type ReactElement } from 'react';
import {
  useDetachProjectFromPack,
  useLinkProjectToPack,
} from '../../hooks/useAdminPacks';
import type { LinkedProject } from '../../schemas/admin.schemas';
import { ProjectSelector } from '@features/workspace/components/ProjectSelector';
import { ServerSelect } from '@features/git/components/ServerSelect';
import { gitApi } from '@features/git/api/gitApi';
import type { GitProject } from '@features/git/types/git.types';

interface PackProjectsSectionProps {
  packId: string;
  linkedProjects: LinkedProject[];
}

export function PackProjectsSection({
  packId,
  linkedProjects,
}: PackProjectsSectionProps): ReactElement {
  const detachProject = useDetachProjectFromPack();
  const linkProject = useLinkProjectToPack();
  const [showRefLookup, setShowRefLookup] = useState(false);
  const [externalRef, setExternalRef] = useState('');
  const [serverId, setServerId] = useState<string | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);

  const linkedIds = linkedProjects.map((lp) => lp.project_id);

  const handleUnlink = (projectId: string): void => {
    if (!window.confirm('Unlink this project from the pack?')) return;
    detachProject.mutate(projectId);
  };

  const link = (project: GitProject): void => {
    linkProject.mutate({
      projectId: project.id,
      packId,
      projectName: project.path,
    });
  };

  const handleSelect = (project: GitProject | null): void => {
    if (project) link(project);
  };

  const handleManualLookup = (): void => {
    if (!externalRef.trim() || !serverId) {
      setLookupError('Server and external ref are required');
      return;
    }
    setIsLookingUp(true);
    setLookupError(null);
    void (async (): Promise<void> => {
      try {
        const project = await gitApi.lookupProject(serverId, externalRef);
        if (linkedIds.includes(project.id)) {
          setLookupError(`${project.path} is already linked to this pack`);
          return;
        }
        link(project);
        setExternalRef('');
      } catch (error) {
        setLookupError(
          error instanceof Error ? error.message : 'Failed to lookup project'
        );
      } finally {
        setIsLookingUp(false);
      }
    })();
  };

  return (
    <div className="audit-log pack-projects">
      <h3 className="audit-log__title">
        Linked Projects
        {linkedProjects.length > 0 && (
          <span className="pack-projects__count">{linkedProjects.length}</span>
        )}
      </h3>

      <div className="pack-projects__toolbar">
        <div className="pack-projects__add-row">
          <span className="pack-projects__add-label">Add project</span>
          <div className="pack-projects__picker">
            <ProjectSelector
              selectedProject={null}
              onSelect={handleSelect}
              disabled={linkProject.isPending}
              excludeProjectIds={linkedIds}
            />
          </div>
          <button
            type="button"
            className="pack-projects__ref-toggle"
            aria-expanded={showRefLookup}
            onClick={() => { setShowRefLookup(!showRefLookup); setLookupError(null); }}
          >
            {showRefLookup ? 'Hide ref lookup' : 'By external ref'}
          </button>
        </div>

        {showRefLookup && (
          <div className="pack-projects__add-row">
            <span className="pack-projects__add-label">External ref</span>
            <div className="pack-projects__picker form-select">
              <div className="form-select__compound">
                <ServerSelect value={serverId} onChange={setServerId} requireCredential compact />
                <span className="form-select__divider" aria-hidden="true" />
                <input
                  type="text"
                  value={externalRef}
                  onChange={(e) => { setExternalRef(e.target.value); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleManualLookup(); }}
                  placeholder="owner/repo or numeric id"
                  aria-label="External ref"
                  className="pack-projects__ref-input"
                />
              </div>
            </div>
            <button
              type="button"
              className="feature-table__edit-btn"
              onClick={handleManualLookup}
              disabled={!externalRef.trim() || !serverId || isLookingUp || linkProject.isPending}
            >
              {isLookingUp ? 'Looking up…' : 'Link'}
            </button>
          </div>
        )}

        {lookupError && (
          <p className="pack-projects__error" role="alert">{lookupError}</p>
        )}
      </div>

      <table className="feature-table__table pack-projects__table">
        <thead>
          <tr>
            <th>Project</th>
            <th>Linked By</th>
            <th className="pack-projects__actions-col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {linkedProjects.length === 0 ? (
            <tr>
              <td colSpan={3} className="feature-table__empty">
                No projects linked to this pack.
              </td>
            </tr>
          ) : (
            linkedProjects.map((lp) => (
              <tr key={lp.project_id}>
                <td>{lp.project_name}</td>
                <td>{lp.linked_by}</td>
                <td className="pack-projects__actions-col">
                  <button type="button" className="feature-table__edit-btn feature-table__edit-btn--danger"
                    onClick={() => { handleUnlink(lp.project_id); }}
                    disabled={detachProject.isPending}>
                    Unlink
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
